import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { FolderUp, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { api } from "../lib/api";
import { DOC_TYPES } from "../lib/constants";
import { normSearch } from "../lib/searchText";

const OK_EXT = /\.(jpe?g|png|webp|heic|heif|pdf)$/i;
const BAD_EXT = /\.(docx?|xlsx?|pptx?|txt|csv|zip|rar|mp4|mov|ini|db)$/i;
const okFile = (f) => OK_EXT.test(f.name) || /^image\/|pdf/i.test(f.type || "") || (!BAD_EXT.test(f.name) && !(f.type || "").length);
const RULES = [
  ["passport", /جواز|passport/i], ["tashark", /تشارك|أجير|اجير|tashark|ajeer/i], ["iqama", /إقام|اقام|هوية|هويه|iqama|resid/i],
  ["medical_insurance", /تأمين|تامين|insurance/i], ["delivery_authorization", /تفويض|authori/i], ["health_card", /صحي|بلدي|health/i],
  ["vehicle_registration", /استمار|رخصة سير|رخصه سير|registr/i], ["driving_license", /رخص|licen/i], ["driver_card", /سائق|driver/i],
  ["operation_card", /تشغيل|operat/i], ["contract", /عقد|contract/i],
];
const keyOf = (n) => (RULES.find(([, re]) => re.test(n)) || [""])[0];
const dg = (v) => String(v || "").replace(/\D/g, "");

const matchEmp = (folder, emps) => {
  const d = (folder.match(/\d{4,}/) || [])[0];
  if (d) {
    const e = emps.find((x) => dg(x.iqama_number) === d) || emps.find((x) => dg(x.employee_number) === d || String(x.rider_id || "") === d);
    if (e) return e.id;
  }
  const q = normSearch(folder.replace(/[\d_\-./]+/g, " "));
  if (q.length < 5) return "";
  const hit = emps.filter((x) => normSearch(x.name) === q);
  if (hit.length === 1) return hit[0].id;
  const part = emps.filter((x) => { const n = normSearch(x.name); return n && n.length >= 5 && (n.includes(q) || q.includes(n)); });
  return part.length === 1 ? part[0].id : "";
};

const build = (files, emps, fixedEmp) => files.filter(okFile).map((f) => {
  const p = (f.webkitRelativePath || f.name).split("/");
  const folder = p.length > 1 ? p[p.length - 2] : "";
  const employee_id = fixedEmp || matchEmp(folder, emps) || matchEmp(f.name.replace(/\.[^.]+$/, ""), emps);
  return { file: f, folder: folder || "—", employee_id, doc_key: keyOf(f.name), expiry_date: "", st: "new" };
});

const shrink = async (file) => {
  if (!/^image\/(jpe?g|png|webp)$/i.test(file.type) || file.size < 1200000) return file;
  try {
    const bmp = await createImageBitmap(file);
    const k = Math.min(1, 1800 / Math.max(bmp.width, bmp.height));
    const cv = document.createElement("canvas");
    cv.width = Math.round(bmp.width * k); cv.height = Math.round(bmp.height * k);
    cv.getContext("2d").drawImage(bmp, 0, 0, cv.width, cv.height);
    const blob = await new Promise((res) => cv.toBlob(res, "image/jpeg", 0.85));
    return blob && blob.size < file.size ? new File([blob], file.name.replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" }) : file;
  } catch { return file; }
};

const ST = { new: "", busy: "جارٍ…", ok: "جاهز", err: "خطأ", saved: "تم الحفظ" };
const stem = (n) => String(n || "").replace(/\.[^.]+$/, "").trim();

const Row = ({ r, i, emps, upd, ctypes, addType }) => (
  <tr className={`border-b ${!r.employee_id || !r.doc_key ? "bg-amber-50" : ""}`} data-testid={`docf-row-${i}`}>
    <td className="max-w-[110px] truncate p-1.5 font-semibold" title={r.folder}>{r.folder}</td>
    <td className="max-w-[120px] truncate p-1.5 text-slate-500" title={r.file.name}>{r.file.name}</td>
    <td className="p-1.5"><select className="h-7 max-w-[150px] rounded border px-1 text-xs" value={r.employee_id} onChange={(e) => upd(i, { employee_id: e.target.value })} data-testid={`docf-emp-${i}`}>
      <option value="">— اختر —</option>{emps.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}</select></td>
    <td className="p-1.5"><select className="h-7 rounded border px-1 text-xs" value={r.ctype ? `c:${r.label}` : r.doc_key}
      onChange={async (e) => {
        const v = e.target.value;
        if (v === "__new") { const n = await addType(); if (n) upd(i, { doc_key: "other", label: n, ctype: true }); return; }
        if (v.startsWith("c:")) { upd(i, { doc_key: "other", label: v.slice(2), ctype: true }); return; }
        upd(i, { doc_key: v, ctype: false, label: v === "other" ? (r.ctype ? stem(r.file.name) : r.label || stem(r.file.name)) : r.label });
      }} data-testid={`docf-type-${i}`}>
      <option value="">{r.st === "new" ? "تلقائي (AI)" : "— اختر —"}</option>{DOC_TYPES.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
      {ctypes.length > 0 && <optgroup label="أنواعك">{ctypes.map((c) => <option key={c} value={`c:${c}`}>{c}</option>)}</optgroup>}
      <option value="other">مستند آخر (اكتب الاسم)</option>
      <option value="__new">+ نوع جديد…</option></select>
      {r.doc_key === "other" && !r.ctype && <input className="mt-1 block h-7 w-full max-w-[160px] rounded border px-1 text-xs" value={r.label || ""} onChange={(e) => upd(i, { label: e.target.value })} placeholder="اسم المستند" data-testid={`docf-label-${i}`} />}</td>
    <td className="p-1.5"><input type="date" className="h-7 rounded border px-1 text-xs" value={r.expiry_date || ""} onChange={(e) => upd(i, { expiry_date: e.target.value })} data-testid={`docf-date-${i}`} /></td>
    <td className={`p-1.5 text-[11px] ${r.st === "err" ? "text-red-600" : r.st === "saved" ? "text-emerald-600" : "text-slate-500"}`}>{ST[r.st]}{r.moved && <span className="block text-[10px] text-blue-600" data-testid={`docf-moved-${i}`}>استمارة مفوّض عليها ← الخانة الأساسية</span>}{r.st === "err" && r.msg ? <span className="block max-w-[160px] text-[10px] leading-tight" data-testid={`docf-err-${i}`}>{typeof r.msg === "string" ? r.msg : "خطأ"}</span> : null}</td>
  </tr>
);

const ModePick = ({ employees, onFolder, onFiles }) => {
  const [q, setQ] = useState("");
  const [emp, setEmp] = useState("");
  const list = useMemo(() => { const n = normSearch(q); return n ? employees.filter((e) => normSearch(`${e.name} ${e.iqama_number || ""} ${e.employee_number || ""}`).includes(n)).slice(0, 50) : []; }, [q, employees]);
  return (
    <div className="space-y-4" data-testid="docf-mode">
      <div className="rounded-xl border p-3">
        <p className="mb-2 text-sm font-bold">ملفات موظف واحد</p>
        <Input placeholder="ابحث بالاسم أو رقم الإقامة أو الرقم الوظيفي" value={q} onChange={(e) => { setQ(e.target.value); setEmp(""); }} data-testid="docf-emp-search" />
        {list.length > 0 && !emp && <div className="mt-1 max-h-44 overflow-y-auto rounded border">{list.map((e) => (
          <button key={e.id} className="block w-full border-b px-2 py-1.5 text-right text-xs hover:bg-slate-50" onClick={() => { setEmp(e.id); setQ(e.name); }} data-testid={`docf-emp-opt-${e.id}`}>{e.name} <span className="text-slate-400">{e.iqama_number}</span></button>))}</div>}
        <Button className="mt-2 w-full" disabled={!emp} onClick={() => onFiles(emp)} data-testid="docf-pick-files">اختيار ملفات الموظف</Button>
      </div>
      <div className="rounded-xl border p-3">
        <p className="text-sm font-bold">عدة ملفات لعدة موظفين</p>
        <p className="mb-2 text-[11px] text-slate-500">مجلد رئيسي بداخله مجلد لكل موظف (باسمه أو رقم إقامته أو رقمه الوظيفي). وإذا لم نتعرف على الموظف نقرأ رقم الإقامة من المستند نفسه.</p>
        <Button variant="outline" className="w-full" onClick={onFolder} data-testid="docf-pick-folder">اختيار المجلد</Button>
      </div>
    </div>
  );
};

export const DocFolderImport = ({ employees, onDone }) => {
  const ref = useRef(null);
  const fref = useRef(null);
  const [open, setOpen] = useState(false);
  const [fixed, setFixed] = useState("");
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);
  const [ctypes, setCtypes] = useState([]);
  useEffect(() => { if (open) api.customDocTypes().then(setCtypes).catch(() => {}); }, [open]);
  const addType = async () => {
    const n = (window.prompt("اسم نوع المستند الجديد (مثال: سند لأمر، استقالة)") || "").trim();
    if (!n) return "";
    try { setCtypes(await api.addCustomDocType(n)); toast.success(`تمت إضافة «${n}»`); return n; } catch { toast.error("تعذّر إضافة النوع"); return ""; }
  };
  const upd = (i, p) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...p } : r)));
  const pick = (fl, fx) => { const r = build(Array.from(fl || []), employees, fx); if (!r.length) toast.error("لا توجد ملفات مدعومة (صور/PDF)"); else setRows(r); };
  const byIqama = (n) => (dg(n).length >= 9 ? (employees.find((x) => dg(x.iqama_number) === dg(n)) || {}).id : "") || "";
  const close = () => { if (!busy) { setOpen(false); setRows(null); setFixed(""); } };

  const loop = async (list, fn) => {
    for (let k = 0; k < list.length; k += 1) {
      setBusy({ done: k, total: list.length });
      await fn(list[k]); // sequential on purpose: keeps the server light
    }
    setBusy(null);
  };
  const analyze = () => loop(rows.map((r, i) => [r, i]).filter(([r]) => r.st === "new" || r.st === "err"), async ([r, i]) => {
    upd(i, { st: "busy" });
    let blob;
    try {
      const buf = await r.file.arrayBuffer();
      if (!buf.byteLength) throw new Error("empty");
      blob = new File([buf], r.file.name.includes(".") ? r.file.name : `${r.file.name}.${/pdf/i.test(r.file.type) ? "pdf" : "jpg"}`, { type: r.file.type || "application/octet-stream" });
    } catch { upd(i, { st: "err", msg: "تعذّر قراءة الملف من الجهاز — إن كان من Google Drive نزّله على الجوال أولاً" }); return; }
    try {
      blob = await shrink(blob);
      const d = await api.docFolderAnalyze(blob, r.doc_key).catch(() => new Promise((ok) => setTimeout(ok, 2500)).then(() => api.docFolderAnalyze(blob, r.doc_key)));
      const ae = d.authorized_employee;
      if (ae && r.employee_id && r.employee_id !== ae.id) toast.warning(`«${r.file.name}» مفوّضة على ${ae.name} — ستُحفظ في ملفه بدلاً من المجلد`);
      upd(i, { st: "ok", moved: !!ae, employee_id: ae?.id || r.employee_id || byIqama(d.iqama_number) || (d.name ? matchEmp(d.name, employees) : ""), attachment_id: d.attachment_id, attachment_name: d.attachment_name, doc_key: ae ? d.doc_key : (r.doc_key || d.doc_key || ""), expiry_date: d.expiry_date || "" });
    } catch (e) { upd(i, { st: "err", msg: e?.response?.status >= 500 ? "الخادم انشغل لحظياً — اضغط «قراءة الملفات» مرة أخرى لإعادة الصفوف الفاشلة فقط" : e?.response?.data?.detail || (e?.response ? `خطأ ${e.response.status}` : "انقطع الاتصال أثناء الرفع") }); }
  });
  const save = async () => {
    const list = rows.map((r, i) => [r, i]).filter(([r]) => r.st === "ok" && r.employee_id && r.doc_key);
    if (!list.length) return toast.error("لا توجد صفوف مكتملة للحفظ");
    await loop(list, async ([r, i]) => {
      try { await api.docFolderAttach(r.employee_id, r.doc_key, { attachment_id: r.attachment_id, attachment_name: r.attachment_name, expiry_date: r.expiry_date || null, label: r.label || "" }); upd(i, { st: "saved" }); }
      catch { upd(i, { st: "err" }); }
    });
    toast.success(`تم حفظ ${list.length} مستند`); onDone?.();
  };
  const analyzed = rows?.some((r) => r.st === "ok");
  return (
    <>
      <input ref={ref} type="file" hidden webkitdirectory="" directory="" multiple data-testid="docf-input" onChange={(e) => { pick(e.target.files, ""); e.target.value = ""; }} />
      <input ref={fref} type="file" hidden multiple accept="image/*,application/pdf" data-testid="docf-files-input" onChange={(e) => { pick(e.target.files, fixed); e.target.value = ""; }} />
      <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setOpen(true)} data-testid="docf-open-btn"><FolderUp className="h-4 w-4" /> رفع مستندات الموظفين</Button>
      <Dialog open={open} onOpenChange={(o) => !o && close()}>
        <DialogContent dir="rtl" className={`max-h-[88vh] overflow-y-auto font-body ${rows ? "sm:max-w-4xl" : "sm:max-w-md"}`} data-testid="docf-dialog">
          <DialogHeader className="text-right"><DialogTitle className="font-heading">رفع مستندات الموظفين{rows ? ` (${rows.length} ملف)` : ""}</DialogTitle>
            <DialogDescription>{rows ? "١) «قراءة الملفات» يقرأ النوع والتاريخ بالذكاء الاصطناعي ٢) راجع وعدّل ٣) «حفظ» يستبدل المستند القديم." : "اختر طريقة الرفع"}</DialogDescription></DialogHeader>
          {!rows && <ModePick employees={employees} onFolder={() => ref.current?.click()} onFiles={(id) => { setFixed(id); setTimeout(() => fref.current?.click(), 0); }} />}
          {rows && <><div className="overflow-x-auto"><table className="w-full text-xs"><thead><tr className="border-b bg-slate-50 text-slate-500"><th className="p-1.5 text-right">المجلد</th><th className="p-1.5 text-right">الملف</th><th className="p-1.5 text-right">الموظف</th><th className="p-1.5 text-right">نوع المستند</th><th className="p-1.5 text-right">تاريخ الانتهاء</th><th className="p-1.5" /></tr></thead>
            <tbody>{rows?.map((r, i) => <Row key={i} r={r} i={i} emps={employees} upd={upd} ctypes={ctypes} addType={addType} />)}</tbody></table></div>
          <div className="flex flex-wrap items-center gap-2 pt-2">
            <Button onClick={analyze} disabled={!!busy} variant={analyzed ? "outline" : "default"} data-testid="docf-analyze-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} قراءة الملفات</Button>
            <Button onClick={save} disabled={!!busy || !analyzed} className="bg-emerald-600 hover:bg-emerald-700" data-testid="docf-save-btn">حفظ في ملفات الموظفين</Button>
            {busy && <span className="text-xs text-slate-500" data-testid="docf-progress">{busy.done} / {busy.total}</span>}
            {ctypes.length > 0 && <div className="flex w-full flex-wrap items-center gap-1 pt-1 text-[11px]" data-testid="docf-ctypes">
              <span className="text-slate-500">أنواعك:</span>
              {ctypes.map((c) => <span key={c} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5">{c}
                <button type="button" onClick={async () => { if (window.confirm(`حذف النوع «${c}» من القائمة؟ (المستندات المحفوظة تبقى)`)) setCtypes(await api.delCustomDocType(c)); }} className="text-slate-400 hover:text-red-600" data-testid={`docf-ctype-del-${c}`}>×</button></span>)}
            </div>}
            {analyzed && rows.some((r) => r.st === "ok" && !r.doc_key) && <Button variant="outline" size="sm" disabled={!!busy} data-testid="docf-other-all"
              onClick={() => setRows((rs) => rs.map((r) => (r.st === "ok" && !r.doc_key ? { ...r, doc_key: "other", label: r.label || stem(r.file.name) } : r)))}>الملفات بدون نوع ← «مستند آخر» باسم الملف</Button>}
          </div></>}
        </DialogContent>
      </Dialog>
    </>
  );
};
